import { Tooltip } from 'bootstrap';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CustomWatchlistCardData, CustomWatchlistStage } from '../WatchlistDashboardService';
import AlumniRow from './AlumniRow';
import { buildCustomReportLink } from '../reportLink';
import { STAGE_DESCRIPTIONS_CUSTOM, STAGE_LABELS_CUSTOM } from '../../../watchlists/custom-report/customStage';

const fmtDate = (iso: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** Get tag styling based on layer context (Signal = blue, Strategic = purple) */
const getTagStyles = (layerContext: string) => {
    const layer = layerContext?.toLowerCase() || '';
    if (layer === 'signal') {
        return { bg: 'bg-primary bg-opacity-10', text: 'text-primary' };
    }
    if (layer === 'strategic') {
        return { bg: 'bg-purple-f5f3ff', text: 'text-purple' };
    }
    return { bg: 'bg-secondary bg-opacity-10', text: 'text-secondary' };
};

const FALLBACK_STAGES: CustomWatchlistStage[] = [
    { code: 'identified', label: STAGE_LABELS_CUSTOM.identified, type: 'standard', count: 0 },
    { code: 'validated', label: STAGE_LABELS_CUSTOM.validated, type: 'standard', count: 0 },
    { code: 'warmed', label: STAGE_LABELS_CUSTOM.warmed, type: 'standard', count: 0 },
    { code: 'engaged', label: STAGE_LABELS_CUSTOM.engaged, type: 'standard', count: 0 },
    { code: 'qualified', label: STAGE_LABELS_CUSTOM.qualified, type: 'value', count: 0 },
    { code: 'contribution', label: STAGE_LABELS_CUSTOM.contribution, type: 'value', count: 0 },
    { code: 'dormant', label: STAGE_LABELS_CUSTOM.dormant, type: 'dormant', count: 0 },
    { code: 'denied', label: STAGE_LABELS_CUSTOM.denied, type: 'denied', count: 0 },
];

/** Copied from ActivationCard stageClass. Do not import or edit ActivationCard.tsx for this. */
const stageClass = (type: CustomWatchlistStage['type']) => {
    switch (type) {
        case 'value':
            return 'bg-success bg-opacity-10 text-success border border-success border-opacity-25';
        case 'dormant':
            return 'bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25';
        case 'denied':
            return 'bg-secondary bg-opacity-25 text-dark border border-secondary border-opacity-25';
        default:
            return 'bg-secondary bg-opacity-10 text-dark-blue';
    }
};

/** Custom watchlist card — activation-style pipeline + value zone, plus custom name/tag/edit. */
const CustomWatchlistCard = ({
    card,
    instituteId,
    filterInstituteId,
    onEdit,
    onDelete,
}: {
    card: CustomWatchlistCardData;
    instituteId: string | number;
    filterInstituteId?: string | null;
    onEdit?: (card: CustomWatchlistCardData) => void;
    onDelete?: (card: CustomWatchlistCardData) => void;
}) => {
    const tagStyles = getTagStyles(card.layer_context);
    const pipelineRef = useRef<HTMLDivElement>(null);
    const stages = card.stages?.length ? card.stages : FALLBACK_STAGES;
    const valueZone = card.value_zone || { count: 0, pct: 0 };
    const reportBase = {
        institute_id: instituteId,
        filter_institute_id: filterInstituteId,
        watchlist_id: card.id,
    };

    useEffect(() => {
        if (!pipelineRef.current) return;
        const tooltips = Array.from(pipelineRef.current.querySelectorAll('[data-bs-toggle="tooltip"]'))
            .map((el) => new Tooltip(el as HTMLElement, { container: 'body' }));
        return () => tooltips.forEach((t) => t.dispose());
    }, [card.id, card.stages]);

    return (
    <div className="col">
        <div className="bg-white border rounded-4 p-4 h-100 d-flex flex-column justify-content-between shadow-sm border-success border-opacity-25">
            <div>
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                        <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                            <div className="font-16 font-600 text-dark">{card.name}</div>
                            <span className={`font-10 font-600 py-1 px-2 rounded-pill whitespace-nowrap ${tagStyles.bg} ${tagStyles.text}`}>{card.tag}</span>
                            {onEdit && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-link p-0 text-secondary"
                                    title="Edit watchlist"
                                    onClick={() => onEdit(card)}>
                                    <i className="bi bi-pencil-square font-14"></i>
                                </button>
                            )}
                            {onDelete && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-link p-0 text-danger"
                                    title="Delete watchlist"
                                    onClick={() => onDelete(card)}>
                                    <i className="bi bi-trash font-14"></i>
                                </button>
                            )}
                        </div>
                        <div className="font-11 text-secondary mb-1">
                            {card.created_by_name ? `Created by ${card.created_by_name}` : 'Created'} · {fmtDate(card.created_at)}
                        </div>
                        <div className="font-12 text-secondary mb-2">Alumni progressing through structured engagement stages</div>
                    </div>
                    <div className="text-end flex-shrink-0">
                        <div className="font-24 font-600 text-dark lh-base">{card.count}</div>
                        <div className="font-12 text-secondary">total alumni</div>
                    </div>
                </div>
                {card.description && <div className="font-12 text-secondary mb-3">{card.description}</div>}

                <div ref={pipelineRef} className="d-flex align-items-center gap-1 flex-wrap mb-3">
                    {stages.map((stage, index) => (
                        <div key={stage.code} className="d-flex align-items-center gap-1 flex-fill">
                            <Link
                                to={buildCustomReportLink({ ...reportBase, stage: stage.code })}
                                className={`text-center p-2 rounded-3 flex-fill text-decoration-none ${stageClass(stage.type)}`}
                                style={{ minWidth: 60 }}
                                data-bs-toggle="tooltip"
                                data-bs-title={STAGE_DESCRIPTIONS_CUSTOM[stage.code] ?? stage.label}>
                                <div className="font-18 font-600">{stage.count}</div>
                                <div className="font-10 text-muted text-uppercase">{stage.label}</div>
                            </Link>
                            {index <= 4 && (
                                <span className="align-self-center font-12 text-muted">→</span>
                            )}
                        </div>
                    ))}
                </div>

                <div className="d-flex align-items-center gap-2 py-2 px-3 border rounded-3 bg-success bg-opacity-10 text-success border-success border-opacity-25 mb-3">
                    <span className="font-14">⭐</span>
                    <span className="font-12 font-600">Value Zone — {valueZone.count} alumni (Qualified + Contributing)</span>
                    <span className="ms-auto font-13 text-muted">{valueZone.pct}% of pipeline</span>
                </div>

                <hr className="border-success" />
                <div className="font-10 font-600 text-uppercase text-muted mb-2">Top Alumni in Value Zone</div>
                <div className="d-flex flex-column gap-1">
                    {card.top_alumni.length === 0 ? (
                        <div className="font-12 text-muted">No alumni in the value zone yet</div>
                    ) : (
                        card.top_alumni.map((a) => <AlumniRow key={a.student_id} alumni={a} />)
                    )}
                </div>
            </div>
            {card.count > 0 && (
                <Link
                    to={buildCustomReportLink(reportBase)}
                    className="d-inline-flex align-items-center gap-1 mt-3 font-12 font-600 text-primary text-decoration-none">
                    View Full Pipeline →
                </Link>
            )}
        </div>
    </div>
);
};

export default CustomWatchlistCard;
